// Reusable membership manager for properties and tenancies. Mounted by
// PropertyView / TenancyView via attachMembersPanel(). Owns its own
// fetch + state lifecycle so the host view never has to reason about
// memberships beyond providing the entity id and an "I can manage" hint.

import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { icon } from '../core/icons.js';
import { t } from '../core/i18n.js';

const ROLES = {
  property: ['OWNER', 'ADMIN', 'MANAGER', 'AGENT', 'ACCOUNTANT', 'READ_ONLY'],
  tenancy: ['LANDLORD', 'CO_LANDLORD', 'CO_TENANT', 'GUARANTOR', 'AGENT', 'READ_ONLY'],
};

export class MembersPanel extends Component {
  constructor({
    entity, entityId, canManage, landlordUserId, ownerUserId, onLandlordChange, onCount,
    landlordExternalOwnerId,
  }) {
    super();
    if (entity !== 'property' && entity !== 'tenancy') {
      throw new Error('MembersPanel: entity must be property|tenancy');
    }
    this.entity = entity;
    this.entityId = entityId;
    this.canManage = !!canManage;
    // Reports the member count (accepted + pending) up to the host so the
    // property page can keep the Hozzáférések tab badge live.
    this.onCount = typeof onCount === 'function' ? onCount : null;
    // Property landlord-of-record designation. The current value is held on
    // the component so the radio can update optimistically without having to
    // refetch the parent view's property payload.
    this._ownerUserId = ownerUserId || null;
    this._onLandlordChange = typeof onLandlordChange === 'function' ? onLandlordChange : null;
    this.basePath = entity === 'property'
      ? `/properties/${entityId}/memberships`
      : `/tenancies/${entityId}/memberships`;
    this.state = {
      members: null,
      pending: [],          // tokenised invitations awaiting acceptance
      lastInvite: null,     // { url, email, role } shown as a copy-link banner
      error: null,
      addOpen: false,
      busy: false,
      editingId: null,
      landlordUserId: landlordUserId || null,
      // When an external deed owner is the designated issuer
      // (properties.landlord_external_owner_id) no member radio may read
      // as selected; picking a member clears it server-side.
      landlordExternalOwnerId: landlordExternalOwnerId || null,
    };
  }

  _inviteUrl(token) {
    return `${window.location.origin}/#/invite/${encodeURIComponent(token)}`;
  }

  template() {
    const m = this.state.members;
    const pending = Array.isArray(this.state.pending) ? this.state.pending : [];
    const count = (Array.isArray(m) ? m.length : 0) + pending.length;
    const hasAny = (Array.isArray(m) && m.length > 0) || pending.length > 0;
    return `
      <div data-members-panel>
        <div class="section-head">
          <div class="section-head-titles">
            <div class="section-head-eyebrow">${esc(t('members.title'))}</div>
            <div class="section-head-count mono">${esc(String(count))}</div>
          </div>
          <p class="section-head-sub faint">${esc(t('members.subtitle.' + this.entity))}</p>
        </div>
        ${this.state.error ? `<div class="error-banner">${esc(this.state.error)}</div>` : ''}
        ${this._lastInviteBannerHtml()}
        ${this.canManage && this.state.addOpen ? this._inviteFormHtml() : ''}
        ${m === null
          ? `<div class="muted">${esc(t('members.loading'))}</div>`
          : (!hasAny
              ? (this.canManage && !this.state.addOpen
                  ? `<button type="button" class="add-tile add-tile-empty" data-action="add-member">
                       <span class="add-tile-plus">+</span>
                       <span class="add-tile-label">${esc(t('members.invite'))}</span>
                       <span class="add-tile-hint mono">${esc(t('members.empty'))}</span>
                     </button>`
                  : `<div class="empty">${esc(t('members.empty'))}</div>`)
              : `${this._listHtml(m, pending)}${this.canManage && !this.state.addOpen ? `
                  <button type="button" class="add-tile" data-action="add-member">
                    <span class="add-tile-plus">+</span>
                    <span class="add-tile-label">${esc(t('members.invite'))}</span>
                  </button>` : ''}`)}
      </div>
    `;
  }

  /**
   * After a successful invite of a not-yet-Nestly user, show a one-shot
   * banner with the share-link so the inviter can copy/paste it (no MTA
   * is wired up yet). The token URL points at the SPA's invite-accept
   * view; on accept the user lands in this property at the chosen role.
   */
  _lastInviteBannerHtml() {
    const i = this.state.lastInvite;
    if (!i) return '';
    return `
      <div class="info-banner" data-last-invite>
        <div class="stack-2">
          <strong>${esc(t('members.invite_sent_title') || 'Meghívó elkészült')}</strong>
          <span class="faint">${esc(t('members.invite_sent_body', { email: i.email }) || `${i.email} még nincs Nestlyn — küldd át neki ezt a linket. A regisztráció után automatikusan tagja lesz az ingatlannak.`)}</span>
        </div>
        <div class="row" style="gap: 8px; align-items: center; margin-top: 10px; flex-wrap: wrap;">
          <input type="text" class="input mono" readonly value="${esc(i.url)}" data-invite-url style="flex: 1; min-width: 240px; font-size: 12px;" />
          <button type="button" class="btn btn-sm" data-action="copy-invite-url">${esc(t('common.copy') || 'Másolás')}</button>
          <button type="button" class="btn-mono" data-action="dismiss-last-invite">${esc(t('common.close') || 'Bezár')}</button>
        </div>
      </div>
    `;
  }

  _listHtml(members, pending = []) {
    const accepted = (members || []).map((row, i) => this._rowHtml(row, i));
    const pendingRows = pending.map((p, i) =>
      this._pendingRowHtml(p, (members?.length || 0) + i));
    return `<div class="stack">${[...accepted, ...pendingRows].join('')}</div>`;
  }

  _pendingRowHtml(row, index) {
    const num = String((index ?? 0) + 1).padStart(2, '0');
    const email = row.email || '—';
    const inviter = row.invited_by_name || row.invited_by_email || '';
    const metaBits = [esc((t('members.pending') || 'Pending').toUpperCase())];
    if (inviter) metaBits.push(`${esc((t('members.invited_by') || 'Invited by').toUpperCase())} ${esc(inviter)}`);
    return `
      <div class="unit-card unit-card-pending" data-pending-id="${esc(row.id)}">
        <div class="unit-card-head">
          <span class="unit-card-num mono">${num}</span>
          <div class="unit-card-info">
            <div class="unit-card-name">
              <strong>${esc(email)}</strong>
            </div>
            <div class="unit-card-meta-line mono">${metaBits.join(' · ')}</div>
          </div>
          <div class="unit-card-actions">
            <span class="badge">${esc(t('members.roles.' + (row.role || '')) || row.role || '')}</span>
          </div>
        </div>
      </div>
    `;
  }

  _rowHtml(row, index) {
    const editing = this.state.editingId === row.id;
    const name = row.user_name || row.user_email || row.user_id;
    const num = String((index ?? 0) + 1).padStart(2, '0');
    const metaBits = [];
    if (row.user_email && row.user_name) metaBits.push(esc(row.user_email));
    if (row.invited_at && !row.accepted_at) metaBits.push(esc((t('members.pending') || 'Pending').toUpperCase()));
    const metaHtml = metaBits.length ? `<div class="unit-card-meta-line mono">${metaBits.join(' · ')}</div>` : '';
    return `
      <div class="unit-card" data-member-id="${esc(row.id)}">
        <div class="unit-card-head">
          <span class="unit-card-num mono">${num}</span>
          <div class="unit-card-info">
            <div class="unit-card-name">
              <strong>${esc(name)}</strong>
            </div>
            ${metaHtml}
          </div>
          <div class="unit-card-actions">
            ${this._landlordToggleHtml(row)}
            ${editing
              ? this._roleSelectHtml(row)
              : `<span class="badge">${esc(t('members.roles.' + (row.role || '')) || row.role)}</span>`}
            ${this.canManage && !editing ? `
              <button class="btn-mono" data-action="edit-member" data-id="${esc(row.id)}">${esc(t('common.edit'))}</button>
              <button class="btn-mono danger" data-action="revoke-member" data-id="${esc(row.id)}"
                      aria-label="${esc(t('members.revoke'))}">${icon('trash', { size: 'sm' })}</button>` : ''}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Render the "landlord-of-record" toggle for an OWNER row on a property.
   * Tenancies don't carry the concept; non-OWNER property memberships can't
   * be the landlord (validated server-side in PropertyService::setLandlord).
   */
  _landlordToggleHtml(row) {
    if (this.entity !== 'property') return '';
    // OWNER (operator) and LANDLORD (legal/deed owner) can both be the
    // landlord-of-record. Other roles can't — server-side guard lives in
    // PropertyService::setLandlord.
    const role = (row.role || '').toUpperCase();
    if (role !== 'OWNER' && role !== 'LANDLORD') return '';
    // An external deed owner designated as issuer suppresses the member
    // fallback — no member radio reads as selected in that case.
    const effective = this.state.landlordExternalOwnerId
      ? null
      : (this.state.landlordUserId || this._ownerUserId);
    const isLandlord = effective && String(row.user_id) === String(effective);
    if (!this.canManage) {
      return isLandlord
        ? `<span class="badge badge-accent" title="${esc(t('members.landlord_hint'))}">${esc(t('members.landlord'))}</span>`
        : '';
    }
    const userId = esc(row.user_id || '');
    return `
      <label class="row" style="gap:var(--space-2); align-items:center; margin-right:var(--space-2)">
        <input type="radio" name="property-landlord" data-action="set-landlord"
          data-user-id="${userId}" ${isLandlord ? 'checked' : ''}
          aria-label="${esc(t('members.landlord_aria'))}" />
        <span class="faint">${esc(t('members.landlord'))}</span>
      </label>
    `;
  }

  _roleSelectHtml(row) {
    return `
      <select class="select" data-role-select data-id="${esc(row.id)}">
        ${ROLES[this.entity].map((r) =>
          `<option value="${r}" ${row.role === r ? 'selected' : ''}>${esc(t('members.roles.' + r) || r)}</option>`
        ).join('')}
      </select>
      <button class="btn btn-ghost btn-icon" data-action="cancel-edit" aria-label="${esc(t('common.cancel'))}">
        ${icon('x', { size: 'sm' })}
      </button>
    `;
  }

  _inviteFormHtml() {
    const defaultRole = ROLES[this.entity][0];
    return `
      <form class="stack" data-invite-form>
        <div class="row" style="gap:var(--space-3)">
          <div class="field grow">
            <label>${esc(t('members.form.email'))}</label>
            <input class="input" name="email" type="email" required placeholder="user@example.com" />
          </div>
          <div class="field" style="max-width:180px">
            <label>${esc(t('members.form.role'))}</label>
            <select class="select" name="role">
              ${ROLES[this.entity].map((r) => `<option value="${r}">${esc(t('members.roles.' + r) || r)}</option>`).join('')}
            </select>
          </div>
        </div>
        <p class="faint" data-role-hint>${esc(t('members.role_hints.' + defaultRole) || '')}</p>
        <div class="row" style="justify-content:flex-end; gap:var(--space-3)">
          <button type="button" class="btn btn-ghost" data-action="cancel-add">${esc(t('common.cancel'))}</button>
          <button type="submit" class="btn btn-primary" ${this.state.busy ? 'disabled' : ''}>
            ${esc(t('members.form.submit'))}
          </button>
        </div>
      </form>
    `;
  }

  mount() {
    this.on('[data-action="add-member"]', 'click', () => this.setState({ addOpen: !this.state.addOpen, error: null }));
    this.on('[data-action="cancel-add"]', 'click', () => this.setState({ addOpen: false, error: null }));
    this.on('[data-action="dismiss-last-invite"]', 'click', () => this.setState({ lastInvite: null }));
    this.on('[data-action="copy-invite-url"]', 'click', () => this._copyInviteUrl());
    this.on('[data-action="edit-member"]', 'click', (e) => this.setState({
      editingId: e.currentTarget.getAttribute('data-id'), error: null,
    }));
    this.on('[data-action="cancel-edit"]', 'click', () => this.setState({ editingId: null }));
    this.on('[data-action="revoke-member"]', 'click', (e) => this._revoke(e.currentTarget.getAttribute('data-id')));
    this.on('[data-role-select]', 'change', (e) => this._changeRole(
      e.currentTarget.getAttribute('data-id'), e.currentTarget.value,
    ));
    this.on('[data-action="set-landlord"]', 'change', (e) => this._setLandlord(
      e.currentTarget.getAttribute('data-user-id'),
    ));
    const form = this.el.querySelector('[data-invite-form]');
    if (form) form.addEventListener('submit', (e) => this._invite(e));
    // Explain the picked role inline (READ_ONLY = viewing co-owner, etc.).
    // DOM-only update so typing an email never re-renders the form away.
    const roleSelect = this.el.querySelector('[data-invite-form] select[name="role"]');
    if (roleSelect) {
      roleSelect.addEventListener('change', () => {
        const hint = this.el.querySelector('[data-role-hint]');
        if (hint) hint.textContent = t('members.role_hints.' + roleSelect.value) || '';
      });
    }
    if (this.state.members === null && !this._loadStarted) {
      this._loadStarted = true;
      this._load();
    }
  }

  async _load() {
    try {
      const r = await api.get(this.basePath);
      const members = (r && r.data) || [];
      const pending = (r && Array.isArray(r.pending)) ? r.pending : [];
      this.setState({
        members,
        // Property memberships endpoint also returns pending invitations
        // (tenancy memberships don't yet).
        pending,
        error: null,
      });
      if (this.onCount) this.onCount(members.length + pending.length);
    } catch (err) {
      this.setState({ members: [], pending: [], error: err instanceof ApiError ? err.message : t('members.errors.load') });
    }
  }

  async _invite(e) {
    e.preventDefault();
    const fd = new FormData(e.target);
    const payload = { email: String(fd.get('email') || '').trim(), role: String(fd.get('role') || '') };
    this.setState({ busy: true });
    try {
      const res = await api.post(this.basePath, payload);
      const pendingNew = res && res.pending === true && res.invitation && res.invitation.token
        ? { url: this._inviteUrl(res.invitation.token), email: res.email, role: res.role }
        : null;
      this.setState({
        addOpen: false, busy: false,
        members: null, pending: [],
        lastInvite: pendingNew,
        error: null,
      });
      this._load();
    } catch (err) {
      this.setState({ busy: false, error: err instanceof ApiError ? err.message : t('members.errors.invite') });
    }
  }

  async _copyInviteUrl() {
    const i = this.state.lastInvite;
    if (!i) return;
    try {
      await navigator.clipboard.writeText(i.url);
    } catch {
      const inp = this.el.querySelector('[data-invite-url]');
      if (inp) { inp.select(); document.execCommand('copy'); }
    }
  }

  async _changeRole(id, role) {
    try {
      await api.put(`${this.basePath}/${id}`, { role });
      this.setState({ editingId: null, members: null, error: null });
      this._load();
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('members.errors.change_role') });
    }
  }

  async _setLandlord(userId) {
    if (this.entity !== 'property') return;
    const previous = this.state.landlordUserId;
    const previousExt = this.state.landlordExternalOwnerId;
    // Optimistic update: flip the checked radio immediately, roll back if
    // the server rejects (e.g. user is no longer an OWNER member). Picking
    // a member also clears any external-owner issuer server-side.
    this.setState({ landlordUserId: userId, landlordExternalOwnerId: null, error: null });
    try {
      await api.put(`/properties/${this.entityId}/landlord`, { landlord_user_id: userId });
      if (this._onLandlordChange) {
        try { this._onLandlordChange(userId); } catch {}
      }
    } catch (err) {
      this.setState({
        landlordUserId: previous,
        landlordExternalOwnerId: previousExt,
        error: err instanceof ApiError ? err.message : t('members.errors.set_landlord'),
      });
    }
  }

  async _revoke(id) {
    if (!confirm(t('members.revoke_confirm'))) return;
    try {
      await api.delete(`${this.basePath}/${id}`);
      this.setState({ members: null, error: null });
      this._load();
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('members.errors.revoke') });
    }
  }
}

// Mount or re-mount a MembersPanel into the placeholder element with the
// data-members-slot attribute. Safe to call from a host view's mount().
export function attachMembersPanel(rootEl, props) {
  const slot = rootEl.querySelector('[data-members-slot]');
  if (!slot) return null;
  const panel = new MembersPanel(props);
  panel.render(slot);
  return panel;
}
